import { 
  useQuery, 
  useMutation, 
  useQueryClient,
  UseQueryOptions,
  UseMutationOptions 
} from '@tanstack/react-query';
import { queryKeys, cacheUtils } from '../queryClient';
import * as companyService from '../services/companyService';
import * as rentalService from '../services/rentalService';
import * as deviceService from '../services/deviceService';
import * as contractService from '../services/contractService';
import * as dashboardService from '../services/dashboardService';
import reportService from '../services/reportService';
import { showError, showSuccess } from '../utils/alert';
import { getApiErrorMessage } from '../utils/mapFieldErrors';

// Types
type QueryConfig<T = any> = Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'>;
type MutationConfig<TData = unknown, TVariables = unknown> = Omit<
  UseMutationOptions<TData, Error, TVariables>,
  'mutationFn'
>;

// ==================== Company Hooks ====================
export const useCompanies = (filters?: any, options?: QueryConfig) => {
  return useQuery({
    queryKey: queryKeys.companies.list(filters),
    queryFn: () => companyService.getCompanies(filters),
    ...options,
  });
};

export const useCompany = (id: number, options?: QueryConfig) => {
  return useQuery({
    queryKey: queryKeys.companies.detail(id),
    queryFn: () => companyService.getCompany(id),
    enabled: !!id,
    ...options,
  });
};

export const useCreateCompany = (options?: MutationConfig) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: companyService.createCompany,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
      showSuccess('สร้างข้อมูลบริษัทสำเร็จ');
    },
    onError: (error: any) => {
      showError(getApiErrorMessage(error, 'เกิดข้อผิดพลาดในการสร้างข้อมูลบริษัท'));
    },
    ...options,
  });
};

export const useUpdateCompany = (options?: MutationConfig) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => 
      companyService.updateCompany(id, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.detail((variables as any).id) });
      showSuccess('อัพเดทข้อมูลบริษัทสำเร็จ');
    },
    onError: (error: any) => {
      showError(getApiErrorMessage(error, 'เกิดข้อผิดพลาดในการอัพเดทข้อมูลบริษัท'));
    },
    ...options,
  });
};

export const useSyncFlowAccountCompanies = (options?: MutationConfig) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: companyService.syncFlowAccountCompanies,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.companies.flowAccount });
      showSuccess('ซิงค์ข้อมูลบริษัทจาก Flow Account สำเร็จ');
    },
    onError: (error: any) => {
      showError(getApiErrorMessage(error, 'เกิดข้อผิดพลาดในการซิงค์ข้อมูล'));
    },
    ...options,
  });
};

// ==================== Rental Hooks ====================
export const useRentals = (filters?: any, options?: QueryConfig) => {
  return useQuery({
    queryKey: queryKeys.rentals.list(filters),
    queryFn: () => rentalService.getRentals(filters),
    ...options,
  });
};

export const useRental = (id: number, options?: QueryConfig) => {
  return useQuery({
    queryKey: queryKeys.rentals.detail(id),
    queryFn: () => rentalService.getRental(id),
    enabled: !!id,
    ...options,
  });
};

export const useRentalHistory = (id: number, options?: QueryConfig) => {
  return useQuery({
    queryKey: queryKeys.rentals.history(id),
    queryFn: () => rentalService.getRentalHistory(id),
    enabled: !!id,
    ...options,
  });
};

export const useCreateRental = (options?: MutationConfig) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: rentalService.createRental,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.devices.available });
      queryClient.invalidateQueries({ queryKey: queryKeys.sims.available });
      showSuccess('สร้างข้อมูลการเช่าสำเร็จ');
    },
    onError: (error: any) => {
      showError(getApiErrorMessage(error, 'เกิดข้อผิดพลาดในการสร้างข้อมูลการเช่า'));
    },
    ...options,
  });
};

export const useBulkUpdateRentalStatus = (options?: MutationConfig) => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ ids, status }: { ids: number[]; status: string }) =>
      rentalService.bulkUpdateStatus(ids, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.rentals.all });
      showSuccess('อัพเดทสถานะการเช่าสำเร็จ');
    },
    onError: (error: any) => {
      showError(getApiErrorMessage(error, 'เกิดข้อผิดพลาดในการอัพเดทสถานะ'));
    },
    ...options,
  });
};

// ==================== Device Hooks ====================
export const useDevices = (filters?: any, options?: QueryConfig) => {
  return useQuery({
    queryKey: queryKeys.devices.list(filters),
    queryFn: () => deviceService.getDevices(filters),
    ...options,
  });
};

export const useDevice = (id: number, options?: QueryConfig) => {
  return useQuery({
    queryKey: queryKeys.devices.detail(id),
    queryFn: () => deviceService.getDevice(id),
    enabled: !!id,
    ...options,
  });
};

export const useAvailableDevices = (options?: QueryConfig) => {
  return useQuery({
    queryKey: queryKeys.devices.available,
    queryFn: deviceService.getAvailableDevices,
    ...options,
  });
};

// ==================== Contract Hooks ====================
export const useContracts = (filters?: any, options?: QueryConfig) => {
  return useQuery({
    queryKey: queryKeys.contracts.list(filters),
    queryFn: () => contractService.getContracts(filters),
    ...options,
  });
};

export const useContract = (id: number, options?: QueryConfig) => {
  return useQuery({
    queryKey: queryKeys.contracts.detail(id),
    queryFn: () => contractService.getContract(id),
    enabled: !!id,
    ...options,
  });
};


// ==================== Dashboard Hooks ====================
export const useDashboardSummary = (options?: QueryConfig) => {
  return useQuery({
    queryKey: queryKeys.dashboard.summary,
    queryFn: dashboardService.getDashboardSummary,
    // Refetch ทุก 30 วินาที
    refetchInterval: 30000,
    ...options,
  });
};

export const useDashboardRevenue = (period?: string, options?: QueryConfig) => {
  return useQuery({
    queryKey: queryKeys.dashboard.revenue,
    queryFn: () => dashboardService.getRevenueData(period),
    ...options,
  });
};

export const useDashboardTrends = (options?: QueryConfig) => {
  return useQuery({
    queryKey: queryKeys.dashboard.trends,
    queryFn: dashboardService.getTrendData,
    ...options,
  });
};

export const useDashboardCharts = (options?: QueryConfig) => {
  return useQuery({
    queryKey: queryKeys.dashboard.charts,
    queryFn: dashboardService.getChartData,
    ...options,
  });
};

// ==================== Report Hooks ====================
export const useFinancialReport = (params?: any, options?: QueryConfig) => {
  return useQuery({
    queryKey: queryKeys.reports.financial(params),
    queryFn: () => reportService.getRevenueReport(params),
    ...options,
  });
};

export const useRentalReport = (params?: any, options?: QueryConfig) => {
  return useQuery({
    queryKey: queryKeys.reports.rentals(params),
    queryFn: () => reportService.getDeviceStatusReport(params),
    ...options,
  });
};

export const useDeviceReport = (params?: any, options?: QueryConfig) => {
  return useQuery({
    queryKey: queryKeys.reports.devices(params),
    queryFn: () => reportService.getDeviceStatusReport(params),
    ...options,
  });
};

export const useExpiringItemsReport = (params?: any, options?: QueryConfig) => {
  return useQuery({
    queryKey: queryKeys.reports.expiring(params),
    queryFn: () => reportService.getExpiringItems(params),
    ...options,
  });
};

// ==================== Utility Hooks ====================

// Hook สำหรับ prefetch data ล่วงหน้า
export const usePrefetch = () => {
  const queryClient = useQueryClient();
  
  return {
    prefetchCompanies: async (filters?: any) => {
      await queryClient.prefetchQuery({
        queryKey: queryKeys.companies.list(filters),
        queryFn: () => companyService.getCompanies(filters),
      });
    },
    prefetchRentals: async (filters?: any) => {
      await queryClient.prefetchQuery({
        queryKey: queryKeys.rentals.list(filters),
        queryFn: () => rentalService.getRentals(filters),
      });
    },
    prefetchDashboard: async () => {
      await Promise.all([
        queryClient.prefetchQuery({
          queryKey: queryKeys.dashboard.summary,
          queryFn: dashboardService.getDashboardSummary,
        }),
        queryClient.prefetchQuery({
          queryKey: queryKeys.dashboard.charts,
          queryFn: dashboardService.getChartData,
        }),
      ]);
    },
  };
};

// Hook สำหรับ cache management
export const useCacheManagement = () => {
  return {
    invalidateCompanies: () => cacheUtils.invalidate(queryKeys.companies.all),
    invalidateRentals: () => cacheUtils.invalidate(queryKeys.rentals.all),
    invalidateDevices: () => cacheUtils.invalidate(queryKeys.devices.all),
    invalidateDashboard: () => cacheUtils.invalidateMultiple([
      queryKeys.dashboard.summary,
      queryKeys.dashboard.revenue,
      queryKeys.dashboard.trends,
      queryKeys.dashboard.charts,
    ]),
    // clearAll removed (not exposed on cacheUtils) — caller should use specific invalidate methods
  };
};